import React from "react";
import Dropzone from "react-dropzone";
import { FaFile, FaFileExcel, FaFilePdf, FaFileWord } from "react-icons/fa";
import CrossIcon from "../../../assets/icons/property/cross.svg";

import { customToast } from "../../utility";
import { EMPTY_STRING } from "../../../constants";

const FileUploader = ({
  files,
  onChange,
  handleDelete,
  maxFiles,
  accept = ".jpg, .jpeg, .png",
  disabled = false,
  displayDisabled = true,
}) => {
  // Maximum file size in bytes (5MB)
  const maxSize = 5 * 1024 * 1024;

  // Function to handle the files when dropped or selected
  const onDrop = (acceptedFiles, rejectedFiles) => {
    // Notify the user if any files were rejected
    rejectedFiles.forEach((file) => {
      if (file?.file?.size > maxSize) {
        customToast(
          `${file?.file?.name} exceeds the size limit of 5MB.`,
          "error"
        );
      }
    });

    const updatedFiles = [...files, ...acceptedFiles];

    if (updatedFiles.length > maxFiles) {
      customToast(`You can upload a maximum of ${maxFiles} file(s).`, "error");
      return;
    }

    onChange(updatedFiles);
  };

  return (
    <>
      {/* The main dropzone area where users can drag & drop files */}
      {(!disabled || displayDisabled) && (
        <Dropzone
          onDrop={onDrop}
          accept={accept}
          maxSize={maxSize} // 5MB limit
          disabled={disabled}
        >
          {({ getRootProps, getInputProps, isDragActive }) => (
            <div {...getRootProps()} className="file-uploader-dropzone">
              {/* Hidden file input field used for file selection when clicking on the dropzone */}
              <input {...getInputProps()} aria-label="Upload files" />

              {/* Instructions for the user displayed inside the dropzone */}
              <p className="file-uploader-instructions d-flex gap-2 align-items-center">
                <div className="file-uploader-plus-icon">+</div>
                {isDragActive
                  ? "Drop files here ..."
                  : "Drag & Drop files here or click to select file(s)"}
              </p>

              {/* Subtext indicating file limitations */}
              <small className="file-uploader-limitations">
                Max size 5MB {accept}
                {accept ? " only" : EMPTY_STRING}
              </small>
            </div>
          )}
        </Dropzone>
      )}

      {/* Section to display the selected or uploaded files */}
      <div className="d-flex gap-2 flex-wrap mt-3">
        {files?.map((file, index) => (
          <div
            key={index}
            className="file-uploader-preview-container position-relative"
          >
            {/* Delete button to remove the file */}
            {!disabled && (
              <div
                className="file-uploader-delete-icon pointer"
                onClick={() => handleDelete(index)}
                aria-label="Delete file"
              >
                <img src={CrossIcon} alt="Delete file" />
              </div>
            )}

            {/* Display the file if it's an image (not a PDF) */}
            <a
              href={file?.image ?? EMPTY_STRING}
              target="_blank"
              rel="noreferrer"
              className={`${!file?.image ? "pe-none" : EMPTY_STRING}`}
            >
              {file?.type?.includes("image") ? (
                <img
                  src={URL.createObjectURL(file)}
                  className="file-uploader-image-preview"
                  alt="Uploaded file"
                />
              ) : file?.type?.includes("pdf") ? (
                <FaFilePdf className="file-uploader-pdf-icon" />
              ) : file?.type?.includes("wordprocessingml") ? (
                <FaFileWord className="file-uploader-word-icon" />
              ) : file?.type?.includes("csv") ||
                file?.type?.includes("spreadsheetml") ? (
                <FaFileExcel className="file-uploader-excel-icon" />
              ) : !file?.image ? (
                <FaFile className="file-uploader-file-icon" />
              ) : !file?.image.endsWith(".png") &&
                !file?.image.endsWith(".jpg") &&
                !file?.image.endsWith(".jpeg") &&
                !file?.image.endsWith(".webp") ? (
                <FaFile className="file-uploader-file-icon" />
              ) : (
                <img
                  src={file?.image}
                  className="file-uploader-image-preview"
                  alt="Uploaded file"
                />
              )}
            </a>
          </div>
        ))}
      </div>
    </>
  );
};

export default FileUploader;
